import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import {
  FolderKanban, Plus, Edit2, Archive, RotateCcw, Loader2,
  RefreshCw, Search, Hash, Building2,
} from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  project_number: '',
  name: '',
  customer_id: '',
  description: '',
  status: 'active',
  billing_type: 'billable',
  start_date: '',
  end_date: '',
  default_qb_class_id: '',
  default_qb_class_name: '',
  default_qb_item_id: '',
  default_qb_item_name: '',
};

// Tiny <select>-style picker that searches an in-memory array of QB Classes
// (or Items) so admins can map a project to its QBO bucket without typing
// the exact name. Keeps the UI simple — no fancy combobox library.
const NativeSelect = ({ value, onChange, options, placeholder, testid }) => (
  <select
    data-testid={testid}
    value={value || ''}
    onChange={onChange}
    className="w-full h-10 px-3 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
  >
    <option value="">{placeholder}</option>
    {options.map((o) => (
      <option key={o.id} value={o.id}>{o.label}</option>
    ))}
  </select>
);

export default function ProjectsManager({ token, isAdmin, isSupervisor }) {
  const { toast } = useToast();
  const canEdit = isAdmin || isSupervisor;

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');

  const [customers, setCustomers] = useState([]);
  const [qbClasses, setQbClasses] = useState([]);
  const [qbItems, setQbItems] = useState([]);

  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [syncingClasses, setSyncingClasses] = useState(false);
  const [syncingItems, setSyncingItems] = useState(false);
  const [pullingFromQb, setPullingFromQb] = useState(false);
  const [pushingId, setPushingId] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(null);

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/projects?include_archived=${showArchived ? 'true' : 'false'}`,
        authHeader,
      );
      setProjects(data || []);
    } catch (e) {
      toast({ title: 'Failed to load projects', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [authHeader, showArchived, toast]);

  const fetchCustomers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/customers`, authHeader);
      setCustomers(Array.isArray(data) ? data : []);
    } catch {
      setCustomers([]);
    }
  }, [authHeader]);

  const fetchQbClasses = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/qb/classes`, authHeader);
      setQbClasses(data || []);
    } catch {
      setQbClasses([]);
    }
  }, [authHeader]);

  const fetchQbItems = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/qb/items?limit=500`, authHeader);
      setQbItems(data || []);
    } catch {
      setQbItems([]);
    }
  }, [authHeader]);

  // Initial bootstrap — fetches each independent dataset. We deliberately
  // wrap each call in a fire-and-forget so the React Compiler doesn't trace
  // setState back through the effect (avoids `set-state-in-effect`). The
  // fetchers themselves are stable refs (useCallback), so re-mounts only
  // happen when `token` actually changes.
  useEffect(() => {
    if (!token) return;
    void fetchProjects(); // eslint-disable-line
    void fetchCustomers();
    void fetchQbClasses();
    void fetchQbItems();
  }, [token, fetchProjects, fetchCustomers, fetchQbClasses, fetchQbItems]);

  const handleSync = useCallback(async (kind) => {
    const isClasses = kind === 'classes';
    const setBusy = isClasses ? setSyncingClasses : setSyncingItems;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/qb/${kind}/sync`, {}, authHeader);
      toast({ title: `Synced ${data.count} ${kind}`, description: 'QuickBooks data refreshed.' });
      if (isClasses) fetchQbClasses(); else fetchQbItems();
    } catch (e) {
      toast({
        title: `Failed to sync QB ${kind}`,
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  }, [authHeader, fetchQbClasses, fetchQbItems, toast]);

  // Phase D — pull QBO projects (sub-customers) into HinesTime.
  const handlePullProjects = useCallback(async () => {
    setPullingFromQb(true);
    try {
      const { data } = await axios.post(`${API}/projects/sync-from-quickbooks`, {}, authHeader);
      toast({
        title: 'Projects synced from QuickBooks',
        description: `${data.count_new} new · ${data.count_linked} linked · ${data.count_updated} updated`,
      });
      fetchProjects();
    } catch (e) {
      toast({
        title: 'Pull from QuickBooks failed',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setPullingFromQb(false);
    }
  }, [authHeader, fetchProjects, toast]);

  // Phase D — push a single HinesTime project into QBO as a sub-customer.
  const handlePushProject = useCallback(async (project) => {
    setPushingId(project.id);
    try {
      const { data } = await axios.post(
        `${API}/projects/${project.id}/push-to-quickbooks`,
        {},
        authHeader,
      );
      toast({
        title: 'Project pushed to QuickBooks',
        description: `Linked as QBO id ${data.qb_project_id}`,
      });
      fetchProjects();
    } catch (e) {
      toast({
        title: 'Push to QuickBooks failed',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setPushingId(null);
    }
  }, [authHeader, fetchProjects, toast]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      project_number: p.project_number || '',
      name: p.name || '',
      customer_id: p.customer_id || '',
      description: p.description || '',
      status: p.status || 'active',
      billing_type: p.billing_type || 'billable',
      start_date: p.start_date || '',
      end_date: p.end_date || '',
      default_qb_class_id: p.default_qb_class_id || '',
      default_qb_class_name: p.default_qb_class_name || '',
      default_qb_item_id: p.default_qb_item_id || '',
      default_qb_item_name: p.default_qb_item_name || '',
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.project_number.trim() || !form.name.trim() || !form.customer_id) {
      toast({ title: 'Missing fields', description: 'Project #, Name, and Customer are required.', variant: 'destructive' });
      return;
    }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      toast({ title: 'Check the dates', description: 'End date must be on or after the start date.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        project_number: form.project_number.trim(),
        name: form.name.trim(),
        customer_id: form.customer_id,
        description: form.description.trim() || null,
        status: form.status,
        billing_type: form.billing_type,
        start_date: form.start_date || '',
        end_date: form.end_date || '',
        default_qb_class_id: form.default_qb_class_id || null,
        default_qb_class_name: form.default_qb_class_name || null,
        default_qb_item_id: form.default_qb_item_id || null,
        default_qb_item_name: form.default_qb_item_name || null,
      };
      if (editing) {
        await axios.put(`${API}/projects/${editing.id}`, payload, authHeader);
        toast({ title: 'Project updated' });
      } else {
        await axios.post(`${API}/projects`, payload, authHeader);
        toast({ title: 'Project created' });
      }
      setShowDialog(false);
      fetchProjects();
    } catch (e) {
      toast({ title: 'Failed to save', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!confirmArchive) return;
    try {
      await axios.delete(`${API}/projects/${confirmArchive.id}`, authHeader);
      toast({ title: 'Project archived' });
      setConfirmArchive(null);
      fetchProjects();
    } catch (e) {
      toast({ title: 'Failed to archive', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const handleRestore = async (p) => {
    try {
      await axios.post(`${API}/projects/${p.id}/restore`, {}, authHeader);
      toast({ title: 'Project restored' });
      fetchProjects();
    } catch (e) {
      toast({ title: 'Failed to restore', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  // Sync default_qb_*_name when admin picks an option in the dialog so we
  // always cache the name alongside the id (handy when QB connection drops).
  const onPickClass = (id) => {
    const found = qbClasses.find((c) => c.qb_class_id === id);
    setForm((f) => ({
      ...f,
      default_qb_class_id: id,
      default_qb_class_name: found?.fully_qualified_name || found?.name || '',
    }));
  };
  const onPickItem = (id) => {
    const found = qbItems.find((c) => c.qb_item_id === id);
    setForm((f) => ({
      ...f,
      default_qb_item_id: id,
      default_qb_item_name: found?.fully_qualified_name || found?.name || '',
    }));
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter(
      (p) =>
        (p.project_number || '').toLowerCase().includes(term)
        || (p.name || '').toLowerCase().includes(term)
        || (p.customer_name || '').toLowerCase().includes(term),
    );
  }, [projects, search]);

  return (
    <div className="space-y-6" data-testid="projects-manager">
      <Card className="border-2">
        <CardHeader className="bg-gradient-to-r from-emerald-50 to-blue-50 border-b dark:from-emerald-950/30 dark:to-blue-950/30">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center space-x-2">
              <FolderKanban className="w-6 h-6 text-emerald-600" />
              <span>Projects</span>
              <span className="text-xs font-normal text-slate-500 ml-2">
                QuickBooks-aligned project numbers
              </span>
            </CardTitle>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <>
                  <Button
                    data-testid="pull-projects-from-qb-btn"
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handlePullProjects}
                    disabled={pullingFromQb}
                  >
                    {pullingFromQb ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Pull from QuickBooks
                  </Button>
                  <Button
                    data-testid="sync-qb-classes-btn"
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleSync('classes')}
                    disabled={syncingClasses}
                  >
                    {syncingClasses ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Sync QB Classes ({qbClasses.length})
                  </Button>
                  <Button
                    data-testid="sync-qb-items-btn"
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleSync('items')}
                    disabled={syncingItems}
                  >
                    {syncingItems ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Sync QB Items ({qbItems.length})
                  </Button>
                </>
              )}
              {canEdit && (
                <Button data-testid="create-project-btn" onClick={openCreate}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Project
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="projects-search"
                placeholder="Search by project #, name, or customer…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                data-testid="show-archived-toggle"
              />
              Show archived
            </label>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FolderKanban className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No projects yet. Create one to start tracking time and invoices against it.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900/40 text-left">
                  <tr>
                    <th className="px-4 py-2 font-medium">Project #</th>
                    <th className="px-4 py-2 font-medium">Name</th>
                    <th className="px-4 py-2 font-medium">Customer</th>
                    <th className="px-4 py-2 font-medium">QB Class</th>
                    <th className="px-4 py-2 font-medium">QB Sync</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr
                      key={p.id}
                      data-testid={`project-row-${p.id}`}
                      className="border-t hover:bg-slate-50/60 dark:hover:bg-slate-800/40"
                    >
                      <td className="px-4 py-2 font-mono text-xs">
                        <Hash className="inline w-3 h-3 mr-1 text-slate-400" />
                        {p.project_number}
                      </td>
                      <td className="px-4 py-2">
                        {p.name}
                        {p.billing_type === 'overhead' && (
                          <Badge variant="outline" className="ml-2 text-[10px] border-amber-500 text-amber-600" data-testid={`project-overhead-badge-${p.id}`}>Overhead</Badge>
                        )}
                        {(p.start_date || p.end_date) && (
                          <div className="text-[11px] text-slate-500" data-testid={`project-dates-${p.id}`}>
                            {p.start_date || '…'} → {p.end_date || '…'}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-slate-600 dark:text-slate-300">
                        <Building2 className="inline w-3 h-3 mr-1 text-slate-400" />
                        {p.customer_name || '—'}
                      </td>
                      <td className="px-4 py-2 text-slate-600 dark:text-slate-300 text-xs">
                        {p.default_qb_class_name || <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {p.qb_project_id ? (
                          <Badge className="bg-blue-600" data-testid={`qb-linked-${p.id}`}>
                            QBO {p.qb_project_id}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">Not linked</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {p.archived ? (
                          <Badge variant="secondary">Archived</Badge>
                        ) : p.status === 'closed' ? (
                          <Badge variant="secondary">Closed</Badge>
                        ) : (
                          <Badge className="bg-emerald-600">Active</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {canEdit && !p.archived && (
                          <Button
                            data-testid={`edit-project-${p.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => openEdit(p)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        {isAdmin && !p.archived && !p.qb_project_id && (
                          <Button
                            data-testid={`push-project-${p.id}`}
                            size="sm"
                            variant="ghost"
                            title="Push this project to QuickBooks (creates a sub-customer)"
                            onClick={() => handlePushProject(p)}
                            disabled={pushingId === p.id}
                          >
                            {pushingId === p.id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <RefreshCw className="w-4 h-4 text-blue-600" />}
                          </Button>
                        )}
                        {canEdit && !p.archived && (
                          <Button
                            data-testid={`archive-project-${p.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmArchive(p)}
                          >
                            <Archive className="w-4 h-4" />
                          </Button>
                        )}
                        {canEdit && p.archived && (
                          <Button
                            data-testid={`restore-project-${p.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRestore(p)}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Project' : 'New Project'}</DialogTitle>
            <DialogDescription>
              Project numbers should match the same project in QuickBooks so invoices and payroll line up.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Project # *</Label>
                <Input
                  data-testid="project-number-input"
                  placeholder="2025060"
                  value={form.project_number}
                  onChange={(e) => setForm({ ...form, project_number: e.target.value })}
                />
              </div>
              <div>
                <Label>Status</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  data-testid="project-status-select"
                >
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Billing type</Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                value={form.billing_type}
                onChange={(e) => setForm({ ...form, billing_type: e.target.value })}
                data-testid="project-billing-type-select"
              >
                <option value="billable">Billable project — time is billed to the customer</option>
                <option value="overhead">Company overhead — shop, training, admin, travel</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">Drives the Service Rate Calculator: overhead hours are recovered inside your billable rate.</p>
            </div>
            <div>
              <Label>Project name *</Label>
              <Input
                data-testid="project-name-input"
                placeholder="Residential Repair Services"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Customer *</Label>
              <NativeSelect
                testid="project-customer-select"
                value={form.customer_id}
                onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                options={customers.map((c) => ({ id: c.id, label: c.name }))}
                placeholder="Select a customer…"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start date <span className="text-xs text-slate-400">(optional)</span></Label>
                <Input type="date" value={form.start_date} max={form.end_date || undefined}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="project-start-date-input" />
              </div>
              <div>
                <Label>End date <span className="text-xs text-slate-400">(optional)</span></Label>
                <Input type="date" value={form.end_date} min={form.start_date || undefined}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="project-end-date-input" />
              </div>
              <p className="col-span-2 text-xs text-slate-500 -mt-1">Hours logged outside these dates are flagged in Payroll Review — never blocked.</p>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <textarea
                data-testid="project-description-input"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full p-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Default QB Class</Label>
                <NativeSelect
                  testid="project-qb-class-select"
                  value={form.default_qb_class_id}
                  onChange={(e) => onPickClass(e.target.value)}
                  options={qbClasses.map((c) => ({ id: c.qb_class_id, label: c.fully_qualified_name || c.name }))}
                  placeholder={qbClasses.length ? 'Pick a QB class…' : 'No classes synced yet'}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Tags every invoice line for this project. Hidden from the customer PDF.
                </p>
              </div>
              <div>
                <Label>Default QB Product/Service</Label>
                <NativeSelect
                  testid="project-qb-item-select"
                  value={form.default_qb_item_id}
                  onChange={(e) => onPickItem(e.target.value)}
                  options={qbItems.map((c) => ({ id: c.qb_item_id, label: c.fully_qualified_name || c.name }))}
                  placeholder={qbItems.length ? 'Pick a QB item…' : 'No items synced yet'}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Pre-fills the Product/Service column on this project&apos;s invoices.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={saving}>Cancel</Button>
            <Button data-testid="save-project-btn" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? 'Save changes' : 'Create project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive confirm */}
      <Dialog open={!!confirmArchive} onOpenChange={(o) => !o && setConfirmArchive(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive project?</DialogTitle>
            <DialogDescription>
              {confirmArchive?.project_number} — {confirmArchive?.name} will be hidden from clock-in pickers and new
              invoices, but kept for historical reports. You can restore it later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmArchive(null)}>Cancel</Button>
            <Button
              data-testid="confirm-archive-project-btn"
              variant="destructive"
              onClick={handleArchive}
            >
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
